import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { updateConsultationNote } from '../../../../../routes/doctor/consultation';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { formatAudioOutput } from '../../../../../util/audio/formatAudioOutput';
import CircularProgress from '@mui/material/CircularProgress';
import Microphone from '../microphone/Microphone';
import { setAudioTranscription, setSegments } from '../../../../../redux/features/doctor/Dashboard/consultation/consultationNote';

interface AudioOutputProps {
    isMobile: boolean;
}

const AudioOutput: React.FC<AudioOutputProps> = ({ isMobile }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { consultationNoteId } = useParams();
    const purpose = 'main'; 

    const colors = useAppSelector(state => state.theme.colors);
    const securityPin = useAppSelector(state => state.consultation.securityPin);

    const persistedText = useAppSelector(state => state.consultationNote.audioTranscription);
    const liveSegments = useAppSelector(state => state.consultationNote.segments);
    const isCurrentlyRecording = useAppSelector(state => state.consultationNote.isRecording);
    const isLoading = useAppSelector(state => state.consultationNote.isLoadingTranscription);

    const [displayText, setDisplayText] = useState('');
    const textFieldRef = useRef<HTMLTextAreaElement>(null);
    const preRecordingTextRef = useRef<string>('');
    const recordingStatusRef = useRef<boolean>(isCurrentlyRecording);
    const recordingJustStoppedRef = useRef(false);

    useEffect(() => {
        if (isCurrentlyRecording && !recordingStatusRef.current) { 
            preRecordingTextRef.current = displayText || ''; 
            recordingJustStoppedRef.current = false;
        } else if (!isCurrentlyRecording && recordingStatusRef.current) { 
            recordingJustStoppedRef.current = true;
        }
        recordingStatusRef.current = isCurrentlyRecording;
    }, [isCurrentlyRecording, displayText]); 

    useEffect(() => {
        let newDisplayTextValue = '';

        if (isCurrentlyRecording) {
            const newlyTranscribed = formatAudioOutput(liveSegments) || ''; 
            const baseText = preRecordingTextRef.current || ''; 
            newDisplayTextValue = (baseText ? baseText + (newlyTranscribed && baseText.trim() ? " " : "") : "") + newlyTranscribed;
        } else if (recordingJustStoppedRef.current) {
            const finalTranscribedSegmentsText = formatAudioOutput(liveSegments) || ''; 
            const baseText = preRecordingTextRef.current || ''; 
            const finalCombinedText = (baseText ? baseText + (finalTranscribedSegmentsText && baseText.trim() ? " " : "") : "") + finalTranscribedSegmentsText;
            
            newDisplayTextValue = finalCombinedText;
            dispatch(setAudioTranscription(finalCombinedText));

            if (consultationNoteId && securityPin) {
                updateConsultationNote({ consultationNoteId, securityPin, audioTranscription: finalCombinedText });
            }

            dispatch(setSegments([]));
            preRecordingTextRef.current = ''; 
            recordingJustStoppedRef.current = false;
        } else { 
            newDisplayTextValue = persistedText || '';
        }
        
        setDisplayText(newDisplayTextValue === 'undefined' ? '' : newDisplayTextValue);

    }, [
        isCurrentlyRecording,
        liveSegments,
        persistedText,
        consultationNoteId,
        securityPin,
        dispatch
    ]);
    
    useEffect(() => {
        if (isCurrentlyRecording && textFieldRef.current) {
            const { scrollHeight, clientHeight } = textFieldRef.current;
            textFieldRef.current.scrollTop = scrollHeight - clientHeight;
        }
    }, [displayText, isCurrentlyRecording]);


    const handleTextChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isCurrentlyRecording) return; 
        const newText = e.target.value;
        setDisplayText(newText); 
        dispatch(setAudioTranscription(newText)); 
        if (consultationNoteId && securityPin) {
            updateConsultationNote({ consultationNoteId, securityPin, audioTranscription: newText });
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            position: 'relative',
            marginTop: 2,
        }}>
            <Box sx={{
                display: 'flex', flexDirection: 'row', alignItems: 'center',
                width: '100%', color: colors?.text, marginBottom: 1, fontWeight: '200'
            }}>
                <Box>{t('audioOutput')}</Box>
                {(isLoading || (isCurrentlyRecording && liveSegments.length === 0 && !(displayText || '').trim() )) && (
                     <CircularProgress sx={{ marginLeft: 2, color: colors?.main }} size={15} />
                )}
             </Box>
            <Microphone isMobile={isMobile} purpose={purpose} />
            <TextField
                id="note-audio-output"
                variant="standard"
                value={displayText}
                autoComplete='off'
                multiline={true}
                placeholder={isCurrentlyRecording ? String(t('transcriptionStarting')) : String(t('audioOuputEmpty'))}
                inputRef={textFieldRef}
                sx={{
                    width: '100%',
                    height: 300,
                    maxHeight: '30vh',
                    border: `1px solid ${colors?.borderColor || 'rgb(180, 180, 180)'}`,
                    borderRadius: 2,
                    backgroundColor: colors?.background || '#ffffff',
                    padding: 0,
                    '& .MuiInputBase-root': {
                        height: '100%',
                        padding: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'stretch',
                    },
                    '& .MuiInputBase-input': {
                        padding: theme => theme.spacing(2),
                        fontSize: 14,
                        color: ((displayText || '').trim() || isCurrentlyRecording) ? colors?.text : 'rgb(150, 150, 150)',
                        cursor: isCurrentlyRecording ? 'default' : 'text',
                        overflowY: 'auto !important',
                        height: '100% !important',
                        boxSizing: 'border-box',
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)', borderRadius: '4px', marginBlock: '2px' },
                        '&::-webkit-scrollbar-thumb': { background: colors?.main || '#888', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: colors?.mainHover || '#555' }
                    }
                }}
                InputProps={{
                    disableUnderline: true,
                    readOnly: isCurrentlyRecording,
                }}
                onChange={handleTextChange}
            />
        </Box>
    );
};

export default AudioOutput;